// Payment wire Inquire Response Model

import { IReceiptData, IReceiptObjectData, ReceiptData, ReceiptObjectData, IEmvData, EmvData } from '../common/Receipt';

export interface IInquireResponse {
  // Basic transaction information
  readonly merchid?: string;           // maxLength: 16
  readonly orderid?: string;           // maxLength: 50
  readonly expiry?: string;            // format: MMYY
  readonly amount?: string;            // maxLength: 12, decimal or minor units
  readonly currency?: string;          // maxLength: 3 (e.g. USD)
  readonly retref?: string;            // maxLength: 14
  
  // Response codes and status
  readonly respcode?: string;          // maxLength: 3
  readonly respproc?: string;          // maxLength: 4
  readonly respstat?: string;          // maxLength: 1 (A, B, C)
  readonly resptext?: string;          // maxLength: 40
  
  // Settlement and batch information
  readonly batchid?: string;           // maxLength: 12
  readonly setlstat?: string;          // maxLength: 40
  readonly capturedate?: string;       // maxLength: 14, format: YYYYMMDDHHMMSS
  readonly settledate?: string;        // maxLength: 14, format: YYYYMMDDHHMMSS
  
  // Card and account information
  readonly signature?: string;         // maxLength: 6144, Base64-encoded signature
  readonly token?: string;             // maxLength: 19
  readonly profileid?: string;         // maxLength: 20
  readonly acctid?: string;            // maxLength: 3
  readonly authdate?: string;          // maxLength: 14, format: YYYYMMDD
  readonly lastfour?: string;          // maxLength: 4
  readonly name?: string;              // maxLength: 30
  readonly bintype?: string;           // maxLength: 16
  readonly entrymode?: string;         // maxLength: 25
  
  // Fee information
  readonly fee_authcode?: string;      // maxLength: 6
  readonly fee_format?: string;        // maxLength: 7 (flat, percent)
  readonly fee_merchid?: string;       // maxLength: 16
  readonly fee_retref?: string;        // fee retrieval reference
  readonly fee_type?: string;          // fee type (SURCHRG, CVNFEE, etc.)
  readonly fee_value?: string;         // fee value
  
  // EMV and receipt data (stored as parsed objects internally)
  readonly emvTagData?: IEmvData;    // JSON object from EMV string
  readonly receipt?: IReceiptData;       // JSON object from receipt string
  readonly receiptObj?: IReceiptObjectData;    // JSON object from receiptObj string
  
  // User fields (array of objects or single object)
  readonly userfields?: Array<Record<string, any>> | Record<string, any>;
  
  // Transaction capabilities
  readonly voidable?: string;          // maxLength: 1 (Y, N)
  readonly refundable?: string;        // maxLength: 1 (Y, N)
  
  // Decline information
  readonly declineCategory?: string;   // maxLength: 1 (1, 2, 3)
  readonly declineCategoryText?: string;
  readonly merchAdviceCode?: string;   // maxLength: 2
  readonly merchAdviceText?: string;
}

export class InquireResponse implements IInquireResponse {
  merchid?: string;
  orderid?: string;
  expiry?: string;
  amount?: string;
  currency?: string;
  retref?: string;
  respcode?: string;
  respproc?: string;
  respstat?: string;
  resptext?: string;
  batchid?: string;
  setlstat?: string;
  capturedate?: string;
  settledate?: string;
  signature?: string;
  token?: string;
  profileid?: string;
  acctid?: string;
  authdate?: string;
  lastfour?: string;
  name?: string;
  bintype?: string;
  entrymode?: string;
  fee_authcode?: string;
  fee_format?: string;
  fee_merchid?: string;
  fee_retref?: string;
  fee_type?: string;
  fee_value?: string;
  emvTagData?: EmvData;
  receipt?: ReceiptData;
  receiptObj?: ReceiptObjectData;
  userfields?: Array<Record<string, any>> | Record<string, any>;
  voidable?: string;
  refundable?: string;
  declineCategory?: string;
  declineCategoryText?: string;
  merchAdviceCode?: string;
  merchAdviceText?: string;

  constructor(data: IInquireResponse) {
    this.merchid = data.merchid;
    this.orderid = data.orderid;
    this.expiry = data.expiry;
    this.amount = data.amount;
    this.currency = data.currency;
    this.retref = data.retref;
    this.respcode = data.respcode;
    this.respproc = data.respproc;
    this.respstat = data.respstat;
    this.resptext = data.resptext;
    this.batchid = data.batchid;
    this.setlstat = data.setlstat;
    this.capturedate = data.capturedate;
    this.settledate = data.settledate;
    this.signature = data.signature;
    this.token = data.token;
    this.profileid = data.profileid;
    this.acctid = data.acctid;
    this.authdate = data.authdate;
    this.lastfour = data.lastfour;
    this.name = data.name;
    this.bintype = data.bintype;
    this.entrymode = data.entrymode;
    this.fee_authcode = data.fee_authcode;
    this.fee_format = data.fee_format;
    this.fee_merchid = data.fee_merchid;
    this.fee_retref = data.fee_retref;
    this.fee_type = data.fee_type;
    this.fee_value = data.fee_value;
    
    if (data.emvTagData) {
      this.emvTagData = new EmvData(data.emvTagData);
    }
    if (data.receipt) {
      this.receipt = new ReceiptData(data.receipt);
    }
    if (data.receiptObj) {
      this.receiptObj = new ReceiptObjectData(data.receiptObj);
    }
    this.userfields = data.userfields;
    this.voidable = data.voidable;
    this.refundable = data.refundable;
    this.declineCategory = data.declineCategory;
    this.declineCategoryText = data.declineCategoryText;
    this.merchAdviceCode = data.merchAdviceCode;
    this.merchAdviceText = data.merchAdviceText;
  }

  // Validation helpers
  isValidExpiry(): boolean {
    return !this.expiry || /^\d{4}$/.test(this.expiry);
  }

  isValidAuthDate(): boolean {
    return !this.authdate || /^\d{8}$/.test(this.authdate);
  }

  isValidCaptureDate(): boolean {
    return !this.capturedate || /^\d{14}$/.test(this.capturedate);
  }

  isValidSettleDate(): boolean {
    return !this.settledate || /^\d{14}$/.test(this.settledate);
  }

  isValidRespStat(): boolean {
    return !this.respstat || ['A', 'B', 'C'].includes(this.respstat);
  }

  isValidVoidable(): boolean {
    return !this.voidable || ['Y', 'N'].includes(this.voidable);
  }

  isValidRefundable(): boolean {
    return !this.refundable || ['Y', 'N'].includes(this.refundable);
  }

  isValidDeclineCategory(): boolean {
    return !this.declineCategory || ['1', '2', '3', '4'].includes(this.declineCategory);
  }

  isValidFeeFormat(): boolean {
    return !this.fee_format || ['flat', 'percent'].includes(this.fee_format);
  }

  // Status helpers
  isApproved(): boolean {
    return this.respstat === 'A';
  }

  isDeclined(): boolean {
    return this.respstat === 'C';
  }

  requiresRetry(): boolean {
    return this.respstat === 'B';
  }

  canVoid(): boolean {
    return this.voidable === 'Y';
  }

  canRefund(): boolean {
    return this.refundable === 'Y';
  }

  isSettled(): boolean {
    return !!(this.settledate && this.settledate.length === 14);
  }

  isCaptured(): boolean {
    return !!(this.capturedate && this.capturedate.length === 14);
  }

  hasDeclineAdvice(): boolean {
    return !!(this.declineCategory || this.merchAdviceCode);
  }

  // Amount helpers
  getAmountAsNumber(): number | null {
    if (!this.amount) return null;
    return parseFloat(this.amount);
  }

  getAmountInCents(): number | null {
    const amount = this.getAmountAsNumber();
    return amount !== null ? Math.round(amount * 100) : null;
  }

  // Serialization
  toJSON(): IInquireResponse {
    return {
      ...(this.merchid !== undefined && { merchid: this.merchid }),
      ...(this.orderid !== undefined && { orderid: this.orderid }),
      ...(this.expiry !== undefined && { expiry: this.expiry }),
      ...(this.amount !== undefined && { amount: this.amount }),
      ...(this.currency !== undefined && { currency: this.currency }),
      ...(this.retref !== undefined && { retref: this.retref }),
      ...(this.respcode !== undefined && { respcode: this.respcode }),
      ...(this.respproc !== undefined && { respproc: this.respproc }),
      ...(this.respstat !== undefined && { respstat: this.respstat }),
      ...(this.resptext !== undefined && { resptext: this.resptext }),
      ...(this.batchid !== undefined && { batchid: this.batchid }),
      ...(this.setlstat !== undefined && { setlstat: this.setlstat }),
      ...(this.capturedate !== undefined && { capturedate: this.capturedate }),
      ...(this.settledate !== undefined && { settledate: this.settledate }),
      ...(this.signature !== undefined && { signature: this.signature }),
      ...(this.token !== undefined && { token: this.token }),
      ...(this.profileid !== undefined && { profileid: this.profileid }),
      ...(this.acctid !== undefined && { acctid: this.acctid }),
      ...(this.authdate !== undefined && { authdate: this.authdate }),
      ...(this.lastfour !== undefined && { lastfour: this.lastfour }),
      ...(this.name !== undefined && { name: this.name }),
      ...(this.bintype !== undefined && { bintype: this.bintype }),
      ...(this.entrymode !== undefined && { entrymode: this.entrymode }),
      ...(this.fee_authcode !== undefined && { fee_authcode: this.fee_authcode }),
      ...(this.fee_format !== undefined && { fee_format: this.fee_format }),
      ...(this.fee_merchid !== undefined && { fee_merchid: this.fee_merchid }),
      ...(this.fee_retref !== undefined && { fee_retref: this.fee_retref }),
      ...(this.fee_type !== undefined && { fee_type: this.fee_type }),
      ...(this.fee_value !== undefined && { fee_value: this.fee_value }),
      ...(this.emvTagData !== undefined && { emvTagData: this.emvTagData.toJSON() }),
      ...(this.receipt !== undefined && { receipt: this.receipt.toJSON() }),
      ...(this.receiptObj !== undefined && { receiptObj: this.receiptObj.toJSON() }),
      ...(this.userfields !== undefined && { userfields: this.userfields }),
      ...(this.voidable !== undefined && { voidable: this.voidable }),
      ...(this.refundable !== undefined && { refundable: this.refundable }),
      ...(this.declineCategory !== undefined && { declineCategory: this.declineCategory }),
      ...(this.declineCategoryText !== undefined && { declineCategoryText: this.declineCategoryText }),
      ...(this.merchAdviceCode !== undefined && { merchAdviceCode: this.merchAdviceCode }),
      ...(this.merchAdviceText !== undefined && { merchAdviceText: this.merchAdviceText })
    };
  }

  static fromJSON(data: Record<string, any>): InquireResponse {
    const response = {} as any;

    // Simple string fields
    const stringFields = [
      'merchid', 'orderid', 'expiry', 'amount', 'currency', 'retref',
      'respcode', 'respproc', 'respstat', 'resptext', 'batchid', 'setlstat',
      'capturedate', 'settledate', 'signature', 'token', 'profileid', 'acctid',
      'authdate', 'lastfour', 'name', 'bintype', 'entrymode',
      'fee_authcode', 'fee_format', 'fee_merchid', 'fee_retref', 'fee_type', 'fee_value',
      'voidable', 'refundable', 'declineCategory', 'declineCategoryText',
      'merchAdviceCode', 'merchAdviceText'
    ];

    stringFields.forEach(field => {
      if (data[field] !== undefined && typeof data[field] === 'string') {
        response[field] = data[field];
      }
    });

    // Parse JSON fields
    if (data.emvTagData !== undefined) {
      if (typeof data.emvTagData === 'string') {
        try {
          response.emvTagData = new EmvData(JSON.parse(data.emvTagData));
        } catch {
          response.emvTagData = new EmvData({}); // Fallback for invalid JSON
        }
      } else if (typeof data.emvTagData === 'object') {
        response.emvTagData = new EmvData(data.emvTagData);
      }
    }

    if (data.receipt !== undefined) {
      if (typeof data.receipt === 'string') {
        try {
          response.receipt = new ReceiptData(JSON.parse(data.receipt));
        } catch {
          response.receipt = new ReceiptData({}); // Fallback for invalid JSON
        }
      } else if (typeof data.receipt === 'object') {
        response.receipt = new ReceiptData(data.receipt);
      }
    }

    if (data.receiptObj !== undefined) {
      if (typeof data.receiptObj === 'string') {
        try {
          response.receiptObj = new ReceiptObjectData(JSON.parse(data.receiptObj));
        } catch {
          response.receiptObj = new ReceiptObjectData({}); // Fallback for invalid JSON
        }
      } else if (typeof data.receiptObj === 'object') {
        response.receiptObj = new ReceiptObjectData(data.receiptObj);
      }
    }

    // Handle userfields (can be array or object)
    if (data.userfields !== undefined) {
      if (typeof data.userfields === 'string') {
        try {
          response.userfields = JSON.parse(data.userfields);
        } catch {
          response.userfields = []; // Fallback for invalid JSON
        }
      } else {
        response.userfields = data.userfields;
      }
    }

    return new InquireResponse(response as IInquireResponse);
  }

  // Factory methods
  static createEmpty(): InquireResponse {
    return new InquireResponse({});
  }

  static createDeclined(respcode: string, resptext: string): InquireResponse {
    return new InquireResponse({
      respcode,
      resptext,
      respstat: 'C'
    });
  }

  static createApproved(retref: string, respcode: string): InquireResponse {
    return new InquireResponse({
      retref,
      respcode,
      respstat: 'A',
      resptext: 'Approval'
    });
  }
} 